import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import {
  countExportFiles,
  ExportTemplate,
  ExportTemplateGroup,
  ExportTemplateSelection,
  ExportVariant,
  groupExportTemplates,
  normalizeExportTemplates,
  variantsOf,
} from '../../../shared/model/export-templates';

const TEMPLATE_LABEL_KEYS: Record<ExportTemplate, string> = {
  SPIRIT_TESTING: 'consultant.export.templates.names.SPIRIT_TESTING',
  TEST_SOLUTIONS: 'consultant.export.templates.names.TEST_SOLUTIONS',
  ATOS: 'consultant.export.templates.names.ATOS',
  T_SYSTEMS: 'consultant.export.templates.names.T_SYSTEMS',
  ISO_ITZ_BUND: 'consultant.export.templates.names.ISO_ITZ_BUND',
  ALLIANZ_ADESSO_MITTELSTAND:
    'consultant.export.templates.names.ALLIANZ_ADESSO_MITTELSTAND',
  ALTEN: 'consultant.export.templates.names.ALTEN',
  ERNST_YOUNG_RVSE9: 'consultant.export.templates.names.ERNST_YOUNG_RVSE9',
  BA: 'consultant.export.templates.names.BA',
  PLAIN_TEXT: 'consultant.export.templates.names.PLAIN_TEXT',
};

const GROUP_LABEL_KEYS: Record<ExportTemplateGroup, string> = {
  company: 'consultant.export.templates.groups.company',
  catalogue: 'consultant.export.templates.groups.catalogue',
  further: 'consultant.export.templates.groups.further',
};

const VARIANTS: ExportVariant[] = ['LONG', 'SHORT', 'LONG_ANON', 'SHORT_ANON'];

const VARIANT_LABEL_KEYS: Record<ExportVariant, string> = {
  LONG: 'consultant.export.templates.variants.LONG',
  SHORT: 'consultant.export.templates.variants.SHORT',
  LONG_ANON: 'consultant.export.templates.variants.LONG_ANON',
  SHORT_ANON: 'consultant.export.templates.variants.SHORT_ANON',
};

/** NP20-1285: Vorlagen und Fassungen fuer "Herunterladen" im Profil Export, vor dem Speichern-Modal. */
@Component({
  selector: 'app-export-template-dialog',
  imports: [MatCheckbox, MatIcon, TranslatePipe],
  templateUrl: './export-template-dialog.component.html',
  styleUrl: './export-template-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class ExportTemplateDialogComponent implements OnChanges {
  @Input() visible = false;
  @Input() available: ExportTemplate[] = [];
  @Input() selection: ExportTemplateSelection[] = [];

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() confirmed = new EventEmitter<ExportTemplateSelection[]>();
  @Output() cancelled = new EventEmitter<void>();

  private readonly draft = signal<ExportTemplateSelection[]>([]);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      this.draft.set(normalizeExportTemplates(this.selection, this.available));
    }
  }

  get fileCount(): number {
    return countExportFiles(this.draft());
  }

  get canConfirm(): boolean {
    return this.draft().length > 0;
  }

  get variants(): ExportVariant[] {
    return VARIANTS;
  }

  get groups(): { group: ExportTemplateGroup; templates: ExportTemplate[] }[] {
    return groupExportTemplates(this.available);
  }

  groupLabelKey(group: ExportTemplateGroup): string {
    return GROUP_LABEL_KEYS[group];
  }

  /** Die Textfassung hat keine Fassungswahl und steht als eine Checkbox in der Spalte Lang. */
  offers(template: ExportTemplate, variant: ExportVariant): boolean {
    const variants = variantsOf(template);
    return variants.length > 0
      ? variants.includes(variant)
      : variant === 'LONG';
  }

  templateLabelKey(template: ExportTemplate): string {
    return TEMPLATE_LABEL_KEYS[template];
  }

  variantLabelKey(variant: ExportVariant): string {
    return VARIANT_LABEL_KEYS[variant];
  }

  isTemplateChosen(template: ExportTemplate): boolean {
    return this.draft().some((s) => s.template === template);
  }

  isVariantChosen(template: ExportTemplate, variant: ExportVariant): boolean {
    return this.draft().some(
      (s) => s.template === template && s.variants.includes(variant),
    );
  }

  toggleVariant(template: ExportTemplate, variant: ExportVariant): void {
    this.draft.update((draft) => {
      const current = draft.find((s) => s.template === template);
      if (!current) return [...draft, { template, variants: [variant] }];
      const variants = current.variants.includes(variant)
        ? current.variants.filter((v) => v !== variant)
        : [...current.variants, variant];
      return variants.length > 0
        ? draft.map((s) => (s === current ? { template, variants } : s))
        : draft.filter((s) => s !== current);
    });
  }

  cancel(): void {
    this.cancelled.emit();
    this.onVisibleChange(false);
  }

  confirm(): void {
    if (!this.canConfirm) return;
    this.confirmed.emit(normalizeExportTemplates(this.draft(), this.available));
  }

  onVisibleChange(visible: boolean): void {
    this.visible = visible;
    this.visibleChange.emit(visible);
  }
}
